/// <amd-module name="VSSUI/Components/Tree/TreeList" />
import * as React from "react";

import { ITreeList, ITreeListItem, ITreeListProps } from "../../Components/Tree/Tree.Props";
import { VssDetailsList } from "../../VssDetailsList";

import { DetailsRow, IColumn, IDetailsRowProps } from "OfficeFabric/DetailsList";
import { IObjectWithKey, ISelection, Selection, SelectionMode } from "OfficeFabric/Selection";
import { BaseComponent } from "OfficeFabric/Utilities";
import { Icon } from "VSSUI/Icon";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css, KeyCode } from "VSSUI/Util";

import "VSS/LoaderPlugins/Css!VSSUI/Components/Tree/Tree";

/** @deprecated */
export class TreeList<T> extends BaseComponent<ITreeListProps<T>> implements ITreeList<T> {
    private selection: ISelection;
    private itemsToSelect: string[] | undefined;
    private newlySelectedItem: ITreeListItem<T> | undefined = undefined;

    constructor(props: ITreeListProps<T>) {
        super(props);

        this.selection = new Selection({
            onSelectionChanged: this.onSelectionChanged,
            selectionMode: props.selectionMode,
            getKey: (item: IObjectWithKey) => (item as ITreeListItem<T>).treeItem.id
        });
    }

    public setSelectedItemsById(selectedItemIds: string[] | undefined): void {
        this.selection.setAllSelected(false);
        this.itemsToSelect = selectedItemIds;
        this.setSelection();
    }

    public render(): JSX.Element {
        const {
            className,
            treeListItems,
            primaryTreeColumnKey,
            actionsColumnKey,
            onPrimaryCellClick,
            getMenuItemProviders,
            getMenuItems,
            allocateSpaceForActionsButtonWhileHidden,
            isHeaderVisible,
            initialFocusedIndex,
            onItemInvoked,
            onRowDidMount,
            checkboxVisibility,
            checkButtonAriaLabel,
            ariaLabelForSelectionColumn,
            ariaLabel,
            ariaLabelForGrid
        } = this.props;
        const columns = [...this.props.columns];
        let primaryColumnIndex = 0;
        if (primaryTreeColumnKey !== undefined) {
            for (let i = 0; i < columns.length; i++) {
                if (columns[i].key === primaryTreeColumnKey) {
                    primaryColumnIndex = i;
                    break;
                }
            }
        }

        const initialColumnRender = columns[primaryColumnIndex].onRender;
        const newColumnRender = (item?: ITreeListItem<T>, index?: number, col?: IColumn) => {
            return (
                <TreeListPrimaryCell
                    onItemToggled={this.onItemToggled}
                    treeListItem={item}
                    index={index}
                    column={col}
                    onColumnRender={initialColumnRender}
                    indentPaddingInPixels={20}
                    onClick={onPrimaryCellClick}
                />
            );
        };

        columns[primaryColumnIndex] = { ...columns[primaryColumnIndex], onRender: newColumnRender };

        return (
            <div role="presentation">
                <VssDetailsList
                    className={css("tree-list", className)}
                    isHeaderVisible={isHeaderVisible}
                    items={treeListItems}
                    initialFocusedIndex={initialFocusedIndex}
                    columns={columns}
                    actionsColumnKey={actionsColumnKey}
                    getMenuItems={getMenuItems}
                    allocateSpaceForActionsButtonWhileHidden={allocateSpaceForActionsButtonWhileHidden}
                    getMenuItemProviders={getMenuItemProviders}
                    onRenderRow={this.onRenderRow}
                    usePresentationStyles={true}
                    setKey={"0"}
                    selection={this.selection}
                    selectionPreservedOnEmptyClick={true}
                    onRowDidMount={onRowDidMount}
                    onItemInvoked={onItemInvoked}
                    checkboxVisibility={checkboxVisibility}
                    checkButtonAriaLabel={checkButtonAriaLabel}
                    ariaLabelForSelectionColumn={ariaLabelForSelectionColumn}
                    ariaLabel={ariaLabel}
                    ariaLabelForGrid={ariaLabelForGrid}
                />
            </div>
        );
    }

    public componentDidUpdate() {
        this.setSelection();
    }

    private setSelection() {
        if (this.itemsToSelect) {
            let idsToRetry = [];
            for (const id of this.itemsToSelect) {
                this.selection.setKeySelected(id, true, false);
                if (!this.selection.isKeySelected(id)) {
                    idsToRetry.push(id);
                }
            }

            if (idsToRetry.length === 0) {
                idsToRetry = undefined;
            }
            this.itemsToSelect = idsToRetry;
        }
    }

    private onRenderRow = (props: IDetailsRowProps, renderer: (props: IDetailsRowProps) => JSX.Element | null): JSX.Element | null => {
        const treeListItem = props.item as ITreeListItem<T>;
        if (treeListItem.isExpandable) {
            return (
                <ExpandCollapseRow
                    expanded={treeListItem.isExpanded}
                    treeListItem={treeListItem}
                    rowProps={props}
                    collapse={this.onCollapse}
                    expand={this.onExpand}
                    onKeyDown={this.props.onRowKeyDown}
                />
            );
        }
        return renderer(props);
    };

    private onCollapse = (treeListItem: ITreeListItem<T>) => {
        if (treeListItem.isExpanded) {
            this.props.onItemToggled(treeListItem);
            return true;
        }
        return false;
    };

    private onExpand = (treeListItem: ITreeListItem<T>) => {
        if (!treeListItem.isExpanded) {
            this.props.onItemToggled(treeListItem);
            return true;
        }
        return false;
    };

    private onSelectionChanged = () => {
        const selection = this.selection.getSelection();
        const selectedItems: T[] = [];
        if (selection && selection.length > 0) {
            for (const selectionItem of selection) {
                const selectedTreeListItem = selectionItem as ITreeListItem<T>;
                selectedItems.push(selectedTreeListItem.treeItem.item);
                this.newlySelectedItem = selectedTreeListItem;
            }
            this.itemsToSelect = undefined;
        }

        if (this.props.onSelectedItemsChanged) {
            this.props.onSelectedItemsChanged(selectedItems);
        }
    };

    private onItemToggled = (treeListItem: ITreeListItem<T>, forceCollapse?: boolean): void => {
        if (!treeListItem.isExpanded) {
            this.props.onItemToggled(treeListItem);
        } else {
            if (forceCollapse) {
                this.props.onItemToggled(treeListItem);
            } else if (this.canCollapse(treeListItem)) {
                this.props.onItemToggled(treeListItem);
            } else {
                // The item was just now selected, so we don't want to collapse, but we should next time
                this.newlySelectedItem = undefined;
            }
        }
    };

    private canCollapse = (treeListItem: ITreeListItem<T>): boolean => {
        // Do not collapse the item if it was just now selected
        return this.selection.mode === SelectionMode.none ? true : this.newlySelectedItem !== treeListItem;
    };
}

interface ITreeListPrimaryCellProps<T> {
    onItemToggled: (treeListItem: ITreeListItem<T>, forceCollapse?: boolean) => void;
    treeListItem: ITreeListItem<T>;
    index?: number;
    column?: IColumn;
    onColumnRender: (item?: ITreeListItem<T>, index?: number, column?: IColumn) => JSX.Element;
    indentPaddingInPixels: number;
    onClick?: (event: React.MouseEvent<HTMLElement>, item: T) => void;
}

class TreeListPrimaryCell<T> extends React.Component<ITreeListPrimaryCellProps<T>> {
    public render(): JSX.Element {
        const { treeListItem } = this.props;
        const indentPixels = this.props.indentPaddingInPixels * treeListItem.indentLevel;
        const spinner = treeListItem.isLoading && <Spinner size={SpinnerSize.xSmall} className="tree-list-spinner" />;

        // Even if the item isn't expandable, we will show a hidden icon to keep items aligned.
        const collapseButton = (
            <Icon
                className={css("tree-chevron", !treeListItem.isExpandable && "invisible")}
                iconName={treeListItem.isExpanded ? "ChevronDownSmall" : "ChevronRightSmall"}
                onClick={this.onExpandCollapseButtonClicked}
            />
        );
        return (
            <div
                className="tree-column-wrapper"
                onClick={this.onCellClicked}
                style={{
                    paddingLeft: `${indentPixels}px`
                }}
            >
                {spinner || collapseButton}
                {this.props.onColumnRender(this.props.treeListItem, this.props.index, this.props.column)}
            </div>
        );
    }

    private onExpandCollapseButtonClicked = (event: React.MouseEvent<HTMLElement>): void => {
        event.stopPropagation();
        // Force collapse of the item if the expand collapse button was explicity clicked.
        this.props.onItemToggled(this.props.treeListItem, true);
    };

    private onCellClicked = (event: React.MouseEvent<HTMLElement>): void => {
        if (this.props.onClick) {
            this.props.onClick(event, this.props.treeListItem.treeItem.item);
        } else {
            this.props.onItemToggled(this.props.treeListItem, false);
        }
    };
}

interface IExpandCollapseRowProps<T> {
    expanded: boolean;
    treeListItem: ITreeListItem<T>;
    rowProps: IDetailsRowProps;
    collapse: (treeListItem: ITreeListItem<T>) => boolean;
    expand: (treeListItem: ITreeListItem<T>) => boolean;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>, item: T) => void;
}

class ExpandCollapseRow<T> extends React.Component<IExpandCollapseRowProps<T>> {
    private detailsElement: Element | null;
    private rowComponent: DetailsRow | null;
    private rowElement: HTMLDivElement | null;

    public render(): JSX.Element {
        return (
            <div
                onKeyDown={this.onKeyDown}
                ref={rowElement => {
                    this.rowElement = rowElement;
                }}
            >
                <DetailsRow
                    {...this.props.rowProps}
                    aria-expanded={this.props.expanded}
                    ref={rowComponent => {
                        this.rowComponent = rowComponent;
                    }}
                />
            </div>
        );
    }

    public componentDidMount(): void {
        this.detailsElement = null;
        if (this.rowElement !== null) {
            this.detailsElement = (this.rowElement.querySelector("[tabIndex='0']") as Element) || this.rowElement.children[0];
        }
    }

    private onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.which === KeyCode.rightArrow) {
            if (this.props.expand(this.props.treeListItem)) {
                event.preventDefault();
                event.stopPropagation();
            }
        } else if (event.which === KeyCode.leftArrow) {
            if (this.rowComponent !== null && this.detailsElement !== null && document.activeElement != this.detailsElement) {
                this.rowComponent.focus();
            } else if (this.props.collapse(this.props.treeListItem)) {
                event.preventDefault();
                event.stopPropagation();
            }
        } else if (this.props.onKeyDown) {
            this.props.onKeyDown(event, (this.props.rowProps.item as ITreeListItem<T>).treeItem.item);
        }
    };
}
